import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, Icon, IconButton, Skeleton, Text } from '@chakra-ui/react';
import { MdCheck, MdDelete } from 'react-icons/md';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BASE_CANVAS_W = 148;

const PageThumbnail = ({
  page,
  pdfDoc,
  displayNumber,
  isSelected,
  onSelect,
  onDelete,
  canvasWidth = BASE_CANVAS_W,
  isDragOverlay = false,
}) => {
  const canvasRef = useRef(null);
  const [rendered, setRendered] = useState(false);
  const [canvasH, setCanvasH] = useState(Math.round(canvasWidth * 1.414));
  const [isHovered, setIsHovered] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: page.id,
  });

  const sortableStyle = { transform: CSS.Transform.toString(transform), transition };

  useEffect(() => {
    if (!pdfDoc || isDragOverlay) return;
    setRendered(false);
    setCanvasH(Math.round(canvasWidth * 1.414));
    let cancelled = false;

    const render = async () => {
      try {
        const pdfPage = await pdfDoc.getPage(page.originalIndex + 1);
        const naturalVP = pdfPage.getViewport({ scale: 1 });
        const viewport = pdfPage.getViewport({ scale: canvasWidth / naturalVP.width });
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        setCanvasH(viewport.height);
        await pdfPage.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        if (!cancelled) setRendered(true);
      } catch { /* non-fatal */ }
    };

    render();
    return () => { cancelled = true; };
  }, [pdfDoc, page.originalIndex, isDragOverlay, canvasWidth]);

  return (
    <Box
      ref={isDragOverlay ? undefined : setNodeRef}
      style={isDragOverlay ? {} : sortableStyle}
      opacity={isDragging ? 0.25 : 1}
      userSelect="none"
      {...(isDragOverlay ? {} : { ...attributes, ...listeners })}
    >
      <Box
        bg={isSelected ? 'blue.50' : 'white'}
        borderWidth="2px"
        borderColor={isSelected ? 'blue.400' : isHovered ? 'gray.300' : 'gray.200'}
        boxShadow={
          isDragging
            ? 'none'
            : isSelected
            ? '0 0 0 3px rgba(66,153,225,0.18), 0 4px 12px rgba(0,0,0,0.1)'
            : '0 1px 4px rgba(0,0,0,0.06)'
        }
        borderRadius="14px"
        overflow="visible"
        cursor={isDragging ? 'grabbing' : isHovered ? 'grab' : 'pointer'}
        position="relative"
        transition="border-color 0.15s, box-shadow 0.15s, background 0.15s"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => onSelect(page.id)}
        role="button"
        aria-pressed={isSelected}
        aria-label={`Page ${displayNumber}${isSelected ? ', selected' : ''}`}
      >
        {/* ── Selection circle (top-left, always visible) ── */}
        <Box
          position="absolute"
          top={2}
          left={2}
          zIndex={4}
          w={5}
          h={5}
          borderRadius="full"
          borderWidth="2px"
          borderColor={isSelected ? 'blue.500' : 'blue.300'}
          bg={isSelected ? 'blue.500' : 'white'}
          display="flex"
          alignItems="center"
          justifyContent="center"
          opacity={isSelected || isHovered ? 1 : 0}
          transition="all 0.15s"
          pointerEvents={isSelected || isHovered ? 'auto' : 'none'}
          cursor="pointer"
          onClick={(e) => { e.stopPropagation(); onSelect(page.id); }}
          _hover={{ borderColor: 'blue.400', transform: 'scale(1.5)' }}
        >
          {isSelected && <Icon as={MdCheck} color="white" boxSize={3} />}
        </Box>

        {/* ── Delete button (top-right, on hover) ── */}
        <Box
          position="absolute"
          top={1}
          right={1}
          zIndex={4}
          opacity={isHovered ? 1 : 0}
          transition="opacity 0.15s"
          pointerEvents={isHovered ? 'auto' : 'none'}
        >
          <IconButton
            icon={<MdDelete size={26} />}
            aria-label="Delete page"
            size="sm"
            variant="ghost"
            colorScheme="red"
            borderRadius="lg"
            bg="whiteAlpha.800"
            transition="transform 0.15s, background 0.15s"
            _hover={{ bg: 'red.50', color: 'red.500', transform: 'scale(1.5)' }}
            onClick={(e) => { e.stopPropagation(); onDelete(page.id); }}
          />
        </Box>

        {/* ── Canvas / preview area ── */}
        <Box
          mt={3}
          bg="gray.50"
          borderTopLeftRadius="12px"
          borderTopRightRadius="12px"
          overflow="hidden"
          minH={`${canvasH}px`}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {!rendered && (
            <Skeleton
              w={`${canvasWidth}px`}
              h={`${canvasH}px`}
              startColor="gray.100"
              endColor="gray.200"
              speed={1.2}
            />
          )}
          <canvas
            ref={canvasRef}
            style={{ display: rendered ? 'block' : 'none', maxWidth: '100%' }}
          />
        </Box>

        {/* ── Page number ── */}
        <Flex justify="center" pt={1} pb="4px" px={2} mt="-2px" transform="translateY(-4px)">
          <Text
            as="span"
            fontSize="sm"
            fontWeight="600"
            lineHeight="1"
            color={isSelected ? 'blue.500' : 'gray.500'}
            letterSpacing="0.03em"
            transition="color 0.15s"
          >
            {displayNumber}
          </Text>
        </Flex>
      </Box>
    </Box>
  );
};

export default PageThumbnail;
